// Build a Google Calendar "Add event" URL from an event object.
// Uses all-day event format (YYYYMMDD/YYYYMMDD with end date exclusive).
// If no start_date, returns null.
function fmt(d) {
  return d.replaceAll("-", "");
}

function addDaysISO(iso, n) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function buildGoogleCalendarUrl(ev) {
  if (!ev?.start_date) return null;
  const start = ev.start_date;
  // Google end date is EXCLUSIVE for all-day events
  const endBase = ev.end_date && ev.end_date >= ev.start_date ? ev.end_date : ev.start_date;
  const end = addDaysISO(endBase, 1);
  const dates = `${fmt(start)}/${fmt(end)}`;

  const details = [];
  if (ev.client) details.push(`Client : ${ev.client}`);
  if (ev.contact_name || ev.contact_email || ev.contact_phone) {
    details.push(
      `Contact : ${[ev.contact_name, ev.contact_email, ev.contact_phone].filter(Boolean).join(" — ")}`
    );
  }
  if (ev.budget) details.push(`Budget : ${Number(ev.budget).toLocaleString("fr-FR")} €`);
  if (ev.referent_name) details.push(`Référent AAE : ${ev.referent_name}`);
  if (ev.description) details.push("", ev.description);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.name || "Événement",
    dates,
    details: details.join("\n"),
    location: [ev.location, ev.address].filter(Boolean).join(", "),
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Construit une URL Google Calendar pour un prêt / location matériel.
 * Force l'ouverture sur le compte `contact@antoineevenements.fr` (agenda
 * commun AAE) via le paramètre `authuser`, quel que soit l'utilisateur
 * connecté au moment de l'ajout. Si le navigateur n'est pas connecté à
 * ce compte Google, l'utilisateur sera invité à s'y connecter — l'événement
 * ne finira jamais sur un agenda personnel par erreur.
 */
export function buildLoanGoogleCalendarUrl(loan) {
  if (!loan?.start_date) return null;
  const start = loan.start_date;
  const endBase = loan.end_date && loan.end_date >= loan.start_date ? loan.end_date : loan.start_date;
  const end = addDaysISO(endBase, 1);
  const dates = `${fmt(start)}/${fmt(end)}`;

  const isLoc = loan.type === "location";
  const prefix = isLoc ? "🏷️ Location" : "🎁 Prêt";
  const equipName = loan.equipment?.name || loan.equipment_name || "Matériel";
  const qty = loan.quantity ? ` x${loan.quantity}` : "";
  const text = `${prefix} : ${equipName}${qty} — ${loan.borrower_name || ""}`.trim();

  const details = [];
  details.push(`Type : ${isLoc ? "Location (payante)" : "Prêt (gratuit)"}`);
  details.push(`Matériel : ${equipName}${qty}`);
  if (loan.borrower_name) details.push(`Emprunteur : ${loan.borrower_name}`);
  if (loan.borrower_contact) details.push(`Contact : ${loan.borrower_contact}`);
  if (isLoc && loan.daily_rate) details.push(`Tarif : ${Number(loan.daily_rate).toLocaleString("fr-FR")} € / jour`);
  if (loan.event_name) details.push(`Événement lié : ${loan.event_name}`);
  if (loan.notes) details.push("", loan.notes);

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text,
    dates,
    details: details.join("\n"),
  });

  // `authuser` (email) : hint qui pré-sélectionne le compte AAE si l'utilisateur
  // y est connecté dans son navigateur — sinon Google demande la connexion.
  // On ajoute en dur, jamais l'utilisateur connecté à Compagnon.
  return `https://calendar.google.com/calendar/render?${params.toString()}&authuser=${encodeURIComponent("contact@antoineevenements.fr")}`;
}
